import React, { useState, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { formatCurrency, formatDate, getStatusBadgeStyle } from 'utils/lmmsHelpers';
import type { ApprovalDetailItem } from 'api/types/approval.types';
import { useEnumsByGroup, useSystemConfig } from 'hooks/api';

// Re-export for backward compatibility with dashboard screen
export type ApprovalItem = ApprovalDetailItem;

const RECORD_TYPE_LABELS: Record<string, string> = {
  addon_request: 'Add-On Request',
  closing: 'Closing Transaction',
  combined: 'Combined Transaction',
  regroup: 'Regroup Transaction',
  gl_replacement: 'GL Replacement',
};

const RECORD_TYPE_COLORS: Record<string, { bg: string; color: string }> = {
  addon_request: { bg: 'rgba(59, 130, 246, 0.1)', color: '#2563eb' },
  closing: { bg: 'rgba(239, 68, 68, 0.1)', color: '#dc2626' },
  combined: { bg: 'rgba(139, 92, 246, 0.1)', color: '#7c3aed' },
  regroup: { bg: 'rgba(217, 119, 6, 0.1)', color: '#b45309' },
  gl_replacement: { bg: 'rgba(20, 184, 166, 0.1)', color: '#0d9488' },
};

// ============================================================================
// Helpers
// ============================================================================

const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <div style={{ padding: '0.75rem 0', marginBottom: '0.75rem', borderBottom: '2px solid var(--primary)' }}>
    <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>{title}</h3>
  </div>
);

const getAmountIndicator = (amount: number, eligible: number, maxPermissible: number): { color: string; label: string } => {
  if (amount <= 0 || maxPermissible <= 0) return { color: 'var(--text-muted)', label: '' };
  if (amount <= eligible) return { color: '#16a34a', label: 'Within Eligible' };
  if (amount <= maxPermissible) return { color: '#d97706', label: 'Between Eligible & Max' };
  return { color: '#dc2626', label: 'Exceeds Maximum' };
};

const thStyle: React.CSSProperties = { padding: '0.5rem', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid var(--border-color)' };
const tdStyle: React.CSSProperties = { padding: '0.5rem', borderBottom: '1px solid var(--border-color)' };

// ============================================================================
// Module-Specific Detail Views
// ============================================================================

const AddonRequestDetailView: React.FC<{ record: ApprovalItem }> = ({ record }) => {
  const entries = record.glEntries || [];
  const hasDiffCustomer = entries.some((gl) => gl.glCustomerId !== record.customerId);
  const totalAllocated = entries.reduce((s, gl) => s + (gl.allocatedAmount || 0), 0);

  return (
    <>
      <SectionHeader title="Gold Loans" />
      <div style={{ overflowX: 'auto', marginBottom: '1.5rem' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
          <thead>
            <tr style={{ background: 'var(--bg-tertiary)' }}>
              <th style={{ ...thStyle, textAlign: 'left' }}>#</th>
              {hasDiffCustomer && <th style={{ ...thStyle, textAlign: 'left' }}>GL Customer</th>}
              <th style={{ ...thStyle, textAlign: 'left' }}>GL Number</th>
              <th style={{ ...thStyle, textAlign: 'left' }}>Eligible Weight (g)</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Original Amount</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Alloc. Ratio</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Allocated Amt</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Doc Charge</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Disbursement</th>
              <th style={{ ...thStyle, textAlign: 'left' }}>Loan Date</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((gl, idx) => (
              <tr key={gl.id}>
                <td style={{ ...tdStyle, color: 'var(--text-muted)' }}>{idx + 1}</td>
                {hasDiffCustomer && (
                  <td style={{ ...tdStyle, color: gl.glCustomerId !== record.customerId ? '#d97706' : 'inherit' }}>
                    {gl.glCustomerName} ({gl.glCustomerId})
                  </td>
                )}
                <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{gl.glNumber}</td>
                <td style={{ ...tdStyle, textAlign: 'left' }}>{gl.netWeight.toFixed(3)}</td>
                <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{formatCurrency(gl.originalLoanAmount)}</td>
                <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 500 }}>{((gl.allocationRatio || 0) * 100).toFixed(2)}%</td>
                <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, color: 'var(--primary)' }}>{formatCurrency(gl.allocatedAmount || 0)}</td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>{formatCurrency(gl.documentationCharge ?? 0)}</td>
                <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{formatCurrency(gl.disbursementAmount ?? Math.max(0, gl.originalLoanAmount - (gl.documentationCharge ?? 0)))}</td>
                <td style={tdStyle}>{formatDate(gl.loanDate)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ background: 'var(--bg-tertiary)', fontWeight: 600 }}>
              <td colSpan={hasDiffCustomer ? 3 : 2} style={{ padding: '0.5rem' }}>Total</td>
              <td style={{ padding: '0.5rem', textAlign: 'left' }}>{entries.reduce((s, gl) => s + gl.netWeight, 0).toFixed(3)}</td>
              <td style={{ padding: '0.5rem', textAlign: 'right' }}>{formatCurrency(entries.reduce((s, gl) => s + gl.originalLoanAmount, 0))}</td>
              <td style={{ padding: '0.5rem', textAlign: 'right' }}>100.00%</td>
              <td style={{ padding: '0.5rem', textAlign: 'right', color: 'var(--primary)' }}>{formatCurrency(totalAllocated)}</td>
              <td style={{ padding: '0.5rem', textAlign: 'right' }}>{formatCurrency(entries.reduce((s, gl) => s + (gl.documentationCharge ?? 0), 0))}</td>
              <td style={{ padding: '0.5rem', textAlign: 'right' }}>{formatCurrency(entries.reduce((s, gl) => s + (gl.disbursementAmount ?? Math.max(0, gl.originalLoanAmount - (gl.documentationCharge ?? 0))), 0))}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </>
  );
};

const ClosingDetailView: React.FC<{ record: ApprovalItem }> = ({ record }) => {
  const entries = record.closingGLEntries || [];
  const totalGLP = entries.reduce((s, gl) => s + gl.outstandingPrincipal, 0);
  const totalGLI = entries.reduce((s, gl) => s + gl.outstandingInterest, 0);
  const hasDiffCustomer = entries.some((gl) => gl.customerId !== record.customerId);
  const newRatioTotal = entries.reduce((s, gl) => s + (gl.newAllocationRatio || 0), 0);

  return (
    <>
      {/* GL Entries Table */}
      <SectionHeader title="All Gold Loans" />
      <div style={{ overflowX: 'auto', marginBottom: '1.5rem' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
          <thead>
            <tr style={{ background: 'var(--bg-tertiary)' }}>
              <th style={{ ...thStyle, textAlign: 'left' }}>#</th>
              {hasDiffCustomer && <th style={{ ...thStyle, textAlign: 'left' }}>Customer</th>}
              <th style={{ ...thStyle, textAlign: 'left' }}>GL Number</th>
              <th style={{ ...thStyle, textAlign: 'left' }}>Eligible Weight (g)</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Alloc. Ratio</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>New Alloc. Ratio</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Outstanding Principal</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Outstanding Interest</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Token Loss</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>GL Settlement</th>
              <th style={{ ...thStyle, textAlign: 'left' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((gl, idx) => {
              const isClosing = gl.status === 'MARKED_FOR_CLOSING';
              return (
                <tr key={gl.id} style={isClosing ? { background: 'rgba(239, 68, 68, 0.04)' } : undefined}>
                  <td style={{ ...tdStyle, color: 'var(--text-muted)' }}>{idx + 1}</td>
                  {hasDiffCustomer && (
                    <td style={{ ...tdStyle, color: gl.customerId !== record.customerId ? '#d97706' : 'inherit' }}>
                      {gl.customerName} ({gl.customerId})
                    </td>
                  )}
                  <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{gl.glNumber}</td>
                  <td style={{ ...tdStyle, textAlign: 'left' }}>{gl.netWeight.toFixed(3)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 500 }}>{(gl.allocationRatio * 100).toFixed(2)}%</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 500, color: isClosing ? '#dc2626' : '#16a34a' }}>{((gl.newAllocationRatio || 0) * 100).toFixed(2)}%</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{formatCurrency(gl.outstandingPrincipal)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{formatCurrency(gl.outstandingInterest)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{formatCurrency(gl.tokenLossCharge ?? 0)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{formatCurrency(gl.glSettlement ?? (gl.outstandingPrincipal + gl.outstandingInterest + (gl.tokenLossCharge ?? 0)))}</td>
                  <td style={tdStyle}><span style={getStatusBadgeStyle(gl.status)}>{(gl.status ?? '').replace(/_/g, ' ')}</span></td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ background: 'var(--bg-tertiary)', fontWeight: 600 }}>
              <td colSpan={hasDiffCustomer ? 3 : 2} style={{ padding: '0.5rem' }}>Total</td>
              <td style={{ padding: '0.5rem', textAlign: 'left' }}>{entries.reduce((s, gl) => s + gl.netWeight, 0).toFixed(3)}</td>
              <td style={{ padding: '0.5rem', textAlign: 'right' }}>{(entries.reduce((s, gl) => s + gl.allocationRatio, 0) * 100).toFixed(2)}%</td>
              <td style={{ padding: '0.5rem', textAlign: 'right' }}>{(newRatioTotal * 100).toFixed(2)}%</td>
              <td style={{ padding: '0.5rem', textAlign: 'right' }}>{formatCurrency(totalGLP)}</td>
              <td style={{ padding: '0.5rem', textAlign: 'right' }}>{formatCurrency(totalGLI)}</td>
              <td style={{ padding: '0.5rem', textAlign: 'right' }}>{formatCurrency(entries.reduce((s, gl) => s + (gl.tokenLossCharge ?? 0), 0))}</td>
              <td style={{ padding: '0.5rem', textAlign: 'right' }}>{formatCurrency(entries.reduce((s, gl) => s + (gl.glSettlement ?? (gl.outstandingPrincipal + gl.outstandingInterest + (gl.tokenLossCharge ?? 0))), 0))}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </>
  );
};

const CombinedDetailView: React.FC<{ record: ApprovalItem }> = ({ record }) => {
  const closingGLs = record.closingGLs || [];
  const newLotGLs = record.newLotGLs || [];
  const isRegroup = record.recordType === 'regroup';
  const cThStyle: React.CSSProperties = { padding: '0.4rem', fontWeight: 600, fontSize: '0.7rem', borderBottom: '2px solid var(--border-color)' };

  const closingHasDiffCust = closingGLs.some((gl) => gl.customerId !== (record.closingCustomerId || record.customerId));
  const newLotHasDiffCust = newLotGLs.some((gl) => gl.customerId !== (record.newLotCustomerId || record.customerId));
  const newLotTotalAllocated = newLotGLs.reduce((s, gl) => s + (gl.allocatedAmount || 0), 0);

  return (
    <>
      {/* Closing Leg GL Table */}
      <div style={{ background: 'rgba(239, 68, 68, 0.04)', border: '1px solid rgba(239, 68, 68, 0.2)', borderRadius: '0.5rem', padding: '1rem', marginBottom: '1.5rem' }}>
        <h3 style={{ fontSize: '0.85rem', fontWeight: 700, color: '#dc2626', marginBottom: '0.75rem' }}>
          CLOSING LEG — {record.closingRef}
        </h3>

        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
          <thead>
            <tr style={{ background: 'var(--bg-tertiary)' }}>
              {closingHasDiffCust && <th style={{ ...cThStyle, textAlign: 'left' }}>Customer</th>}
              <th style={{ ...cThStyle, textAlign: 'left' }}>GL #</th>
              <th style={{ ...cThStyle, textAlign: 'right' }}>Eligible Weight (g)</th>
              <th style={{ ...cThStyle, textAlign: 'right' }}>Alloc. Ratio</th>
              <th style={{ ...cThStyle, textAlign: 'right' }}>New Alloc. Ratio</th>
              <th style={{ ...cThStyle, textAlign: 'right' }}>Outstanding Principal</th>
              <th style={{ ...cThStyle, textAlign: 'right' }}>Outstanding Interest</th>
              {isRegroup && <th style={{ ...cThStyle, textAlign: 'right' }}>Token Loss</th>}
              {isRegroup && <th style={{ ...cThStyle, textAlign: 'right' }}>GL Settlement</th>}
              <th style={{ ...cThStyle, textAlign: 'left' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {closingGLs.map((gl, idx) => (
              <tr key={idx} style={{ borderBottom: '1px solid var(--border-color)' }}>
                {closingHasDiffCust && (
                  <td style={{ padding: '0.4rem', color: gl.customerId !== (record.closingCustomerId || record.customerId) ? '#d97706' : 'inherit' }}>
                    {gl.customerName} ({gl.customerId})
                  </td>
                )}
                <td style={{ padding: '0.4rem', fontFamily: 'monospace' }}>{gl.glNumber}</td>
                <td style={{ padding: '0.4rem', textAlign: 'right' }}>{gl.netWeight.toFixed(3)}</td>
                <td style={{ padding: '0.4rem', textAlign: 'right', fontWeight: 500 }}>{(gl.allocationRatio * 100).toFixed(2)}%</td>
                <td style={{ padding: '0.4rem', textAlign: 'right', fontWeight: 500, color: '#dc2626' }}>{((gl.newAllocationRatio || 0) * 100).toFixed(2)}%</td>
                <td style={{ padding: '0.4rem', textAlign: 'right' }}>{formatCurrency(gl.outstandingPrincipal)}</td>
                <td style={{ padding: '0.4rem', textAlign: 'right' }}>{formatCurrency(gl.outstandingInterest)}</td>
                {isRegroup && <td style={{ padding: '0.4rem', textAlign: 'right' }}>{formatCurrency(gl.tokenLossCharge ?? 0)}</td>}
                {isRegroup && <td style={{ padding: '0.4rem', textAlign: 'right', fontWeight: 600 }}>{formatCurrency(gl.glSettlement ?? (gl.outstandingPrincipal + gl.outstandingInterest + (gl.tokenLossCharge ?? 0)))}</td>}
                <td style={{ padding: '0.4rem' }}><span style={getStatusBadgeStyle(gl.status)}>{(gl.status ?? '').replace(/_/g, ' ')}</span></td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ background: 'var(--bg-tertiary)', fontWeight: 600, fontSize: '0.78rem' }}>
              <td colSpan={closingHasDiffCust ? 2 : 1} style={{ padding: '0.4rem' }}>Total</td>
              <td style={{ padding: '0.4rem', textAlign: 'right' }}>{closingGLs.reduce((s, gl) => s + gl.netWeight, 0).toFixed(3)}</td>
              <td style={{ padding: '0.4rem', textAlign: 'right' }}>{(closingGLs.reduce((s, gl) => s + gl.allocationRatio, 0) * 100).toFixed(2)}%</td>
              <td style={{ padding: '0.4rem', textAlign: 'right', color: '#dc2626' }}>{(closingGLs.reduce((s, gl) => s + (gl.newAllocationRatio || 0), 0) * 100).toFixed(2)}%</td>
              <td style={{ padding: '0.4rem', textAlign: 'right' }}>{formatCurrency(closingGLs.reduce((s, gl) => s + gl.outstandingPrincipal, 0))}</td>
              <td style={{ padding: '0.4rem', textAlign: 'right' }}>{formatCurrency(closingGLs.reduce((s, gl) => s + gl.outstandingInterest, 0))}</td>
              {isRegroup && <td style={{ padding: '0.4rem', textAlign: 'right' }}>{formatCurrency(closingGLs.reduce((s, gl) => s + (gl.tokenLossCharge ?? 0), 0))}</td>}
              {isRegroup && <td></td>}
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* New Lot Leg GL Table */}
      <div style={{ background: 'rgba(34, 197, 94, 0.04)', border: '1px solid rgba(34, 197, 94, 0.2)', borderRadius: '0.5rem', padding: '1rem', marginBottom: '1.5rem' }}>
        <h3 style={{ fontSize: '0.85rem', fontWeight: 700, color: '#16a34a', marginBottom: '0.75rem' }}>
          NEW LOT LEG — {record.newRequestRef}
        </h3>

        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
          <thead>
            <tr style={{ background: 'var(--bg-tertiary)' }}>
              {newLotHasDiffCust && <th style={{ ...cThStyle, textAlign: 'left' }}>Customer</th>}
              <th style={{ ...cThStyle, textAlign: 'left' }}>GL #</th>
              <th style={{ ...cThStyle, textAlign: 'right' }}>Weight (g)</th>
              <th style={{ ...cThStyle, textAlign: 'right' }}>Original Amt</th>
              {isRegroup && <th style={{ ...cThStyle, textAlign: 'right' }}>Doc Charge</th>}
              {isRegroup && <th style={{ ...cThStyle, textAlign: 'right' }}>Disbursement</th>}
              <th style={{ ...cThStyle, textAlign: 'right' }}>Ratio</th>
              <th style={{ ...cThStyle, textAlign: 'right' }}>Alloc. Amt</th>
              <th style={{ ...cThStyle, textAlign: 'left' }}>Loan Date</th>
            </tr>
          </thead>
          <tbody>
            {newLotGLs.map((gl, idx) => (
              <tr key={idx} style={{ borderBottom: '1px solid var(--border-color)' }}>
                {newLotHasDiffCust && (
                  <td style={{ padding: '0.4rem', color: gl.customerId !== (record.newLotCustomerId || record.customerId) ? '#d97706' : 'inherit' }}>
                    {gl.customerName} ({gl.customerId})
                  </td>
                )}
                <td style={{ padding: '0.4rem', fontFamily: 'monospace' }}>{gl.glNumber}{isRegroup && gl.sourceMappedGlId && <span style={{ marginLeft: '0.35rem', fontSize: '0.6rem', fontWeight: 600, padding: '0.05rem 0.35rem', borderRadius: '0.5rem', background: '#ccfbf1', color: '#0f766e' }}>Retained</span>}</td>
                <td style={{ padding: '0.4rem', textAlign: 'right' }}>{gl.netWeight.toFixed(3)}</td>
                <td style={{ padding: '0.4rem', textAlign: 'right' }}>{gl.sourceMappedGlId ? '—' : formatCurrency(gl.originalLoanAmount)}</td>
                {isRegroup && <td style={{ padding: '0.4rem', textAlign: 'right' }}>{gl.sourceMappedGlId ? '—' : formatCurrency(gl.documentationCharge ?? 0)}</td>}
                {isRegroup && <td style={{ padding: '0.4rem', textAlign: 'right', fontWeight: 600 }}>{gl.sourceMappedGlId ? '—' : formatCurrency(gl.disbursementAmount ?? (gl.originalLoanAmount - (gl.documentationCharge ?? 0)))}</td>}
                <td style={{ padding: '0.4rem', textAlign: 'right' }}>{((gl.allocationRatio || 0) * 100).toFixed(2)}%</td>
                <td style={{ padding: '0.4rem', textAlign: 'right', fontWeight: 600, color: 'var(--primary)' }}>{formatCurrency(gl.allocatedAmount || 0)}</td>
                <td style={{ padding: '0.4rem' }}>{formatDate(gl.loanDate)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ background: 'var(--bg-tertiary)', fontWeight: 600, fontSize: '0.78rem' }}>
              <td colSpan={newLotHasDiffCust ? 2 : 1} style={{ padding: '0.4rem' }}>Total</td>
              <td style={{ padding: '0.4rem', textAlign: 'right' }}>{newLotGLs.reduce((s, gl) => s + gl.netWeight, 0).toFixed(3)}</td>
              <td style={{ padding: '0.4rem', textAlign: 'right' }}>{formatCurrency(newLotGLs.reduce((s, gl) => s + gl.originalLoanAmount, 0))}</td>
              {isRegroup && <td style={{ padding: '0.4rem', textAlign: 'right' }}>{formatCurrency(newLotGLs.reduce((s, gl) => s + (gl.documentationCharge ?? 0), 0))}</td>}
              {isRegroup && <td style={{ padding: '0.4rem', textAlign: 'right' }}>{formatCurrency(newLotGLs.reduce((s, gl) => s + (gl.disbursementAmount ?? 0), 0))}</td>}
              <td style={{ padding: '0.4rem', textAlign: 'right' }}>100.00%</td>
              <td style={{ padding: '0.4rem', textAlign: 'right', color: 'var(--primary)' }}>{formatCurrency(newLotTotalAllocated)}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </>
  );
};

// ============================================================================
// Regroup Approval Detail View — mirrors the approved mock-up (view-approve)
// ============================================================================

const RegroupDetailView: React.FC<{ record: ApprovalItem; roundingUnit: number }> = ({ record, roundingUnit }) => {
  const closingGLs = record.closingGLs || [];
  const newLotGLs = record.newLotGLs || [];

  const th: React.CSSProperties = { padding: '0.4rem', fontWeight: 600, fontSize: '0.68rem', borderBottom: '2px solid var(--border-color)' };
  const kK: React.CSSProperties = { fontSize: '0.68rem', color: 'var(--text-muted)', marginBottom: '0.15rem' };
  const kV: React.CSSProperties = { fontWeight: 600, fontSize: '0.82rem' };
  const cardStyle = (rgb: string): React.CSSProperties => ({
    background: `rgba(${rgb}, 0.04)`, border: `1px solid rgba(${rgb}, 0.2)`,
    borderRadius: '0.5rem', padding: '1rem', marginBottom: '1.25rem',
  });
  const chip = (bg: string, color: string, text: string) => (
    <span style={{ fontSize: '0.62rem', fontWeight: 600, padding: '0.05rem 0.45rem', borderRadius: '0.625rem', background: bg, color }}>{text}</span>
  );

  // ---- Closing leg derivations (mock formulas) ----
  const glSettlementOf = (gl: any) => gl.glSettlement ?? ((gl.outstandingPrincipal || 0) + (gl.outstandingInterest || 0) + (gl.tokenLossCharge || 0));
  const isClosedGl = (gl: any) =>
    ((gl.outstandingPrincipal || 0) + (gl.outstandingInterest || 0) + (gl.tokenLossCharge || 0) + (gl.glSettlement || 0)) > 0 ||
    (gl.status || '').toUpperCase().includes('CLOS') || (gl.status || '').toUpperCase().includes('MARK');

  const closedGLs = closingGLs.filter(isClosedGl);
  const closedGlSettlement = closedGLs.reduce((s, gl) => s + glSettlementOf(gl), 0);
  const closedRatioSum = closedGLs.reduce((s, gl) => s + (gl.allocationRatio || 0), 0);
  const addonOutP = record.addonOutstandingPrincipal || 0;
  const addonSettlementFull = addonOutP + (record.addonOutstandingInterest || 0);
  const closedPrincipalPortion = Math.round(addonOutP * closedRatioSum);
  const totalClosingLeg = closedGlSettlement + addonSettlementFull;

  // ---- New lot leg derivations ----
  const gross = record.newLotGrossValue || 0;
  const eligible = record.newLotEligibleAmount || 0;
  const newGlLoanTotal = newLotGLs.filter((g) => !g.sourceMappedGlId).reduce((s, g) => s + (g.originalLoanAmount || 0), 0);
  const newGlEligibility = gross - newGlLoanTotal;
  const carried = eligible - newGlEligibility;
  const requested = record.newLotRequestedAmount || 0;
  const newGlDisbursement = newLotGLs
    .filter((g) => !g.sourceMappedGlId)
    .reduce((s, g) => s + (g.disbursementAmount ?? ((g.originalLoanAmount || 0) - (g.documentationCharge || 0))), 0);
  const newTotalWeight = newLotGLs.reduce((s, g) => s + (g.netWeight || 0), 0);
  // Weight for the Gross Gold Value display — matches the server-persisted
  // gross_value formula (new-only rows × rate). Excludes retained rows since
  // their gold value is already counted in the closing leg.
  const newLotNewGlWeight = newLotGLs
    .filter((g) => !g.sourceMappedGlId)
    .reduce((s, g) => s + (g.netWeight || 0), 0);
  const ncf = Math.round(record.netCashFlow ?? ((requested + newGlDisbursement) - totalClosingLeg));

  return (
    <>
      {/* ---------------- Closing Leg ---------------- */}
      <div style={cardStyle('239, 68, 68')}>
        <h3 style={{ fontSize: '0.85rem', fontWeight: 700, color: '#dc2626', margin: '0 0 0.75rem 0' }}>
          Closing Leg — {record.closingMappingRef || record.mappingRef || '-'}
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem', marginBottom: '0.75rem' }}>
          <div><div style={kK}>Customer</div><div style={kV}>{(record.closingCustomerName || record.customerName)} ({record.closingCustomerId || record.customerId})</div></div>
          <div><div style={kK}>Add-On Account</div><div style={{ ...kV, fontFamily: 'monospace' }}>{record.closingLoanAccountNumber || record.loanAccountNumber || '-'}</div></div>
          <div>
            <div style={kK}>Add-On Disbursed</div>
            <div style={kV}>
              {record.closingAddonLoanDate ? formatDate(record.closingAddonLoanDate) : '-'}{' '}
              {record.closingAfterCutoff
                ? chip('#dcfce7', '#15803d', `✓ after cut-off${record.regroupCutoffDate ? ' ' + formatDate(record.regroupCutoffDate) : ''}`)
                : (record.closingAddonLoanDate ? chip('#fee2e2', '#b91c1c', 'on/before cut-off') : null)}
            </div>
          </div>
          <div><div style={kK}>Add-On Settlement (full P+I)</div><div style={kV}>{formatCurrency(addonSettlementFull)}</div></div>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
          <thead>
            <tr style={{ background: 'var(--bg-tertiary)' }}>
              <th style={{ ...th, textAlign: 'left' }}>GL #</th>
              <th style={{ ...th, textAlign: 'left' }}>Action</th>
              <th style={{ ...th, textAlign: 'right' }}>Weight</th>
              <th style={{ ...th, textAlign: 'right' }}>Ratio</th>
              <th style={{ ...th, textAlign: 'right' }}>O/S Principal</th>
              <th style={{ ...th, textAlign: 'right' }}>O/S Interest</th>
              <th style={{ ...th, textAlign: 'right' }}>Token Loss</th>
              <th style={{ ...th, textAlign: 'right' }}>GL Settlement</th>
            </tr>
          </thead>
          <tbody>
            {closingGLs.map((gl, idx) => {
              const closed = isClosedGl(gl);
              return (
                <tr key={idx} style={{ borderBottom: '1px solid var(--border-color)', background: closed ? undefined : 'rgba(20,184,166,0.05)' }}>
                  <td style={{ padding: '0.4rem', fontFamily: 'monospace' }}>{gl.glNumber}</td>
                  <td style={{ padding: '0.4rem' }}>{closed ? chip('#fee2e2', '#b91c1c', 'Close') : chip('#ccfbf1', '#0f766e', 'Retain → new lot')}</td>
                  <td style={{ padding: '0.4rem', textAlign: 'right' }}>{(gl.netWeight || 0).toFixed(3)}</td>
                  <td style={{ padding: '0.4rem', textAlign: 'right' }}>{((gl.allocationRatio || 0) * 100).toFixed(2)}%</td>
                  <td style={{ padding: '0.4rem', textAlign: 'right' }}>{closed ? formatCurrency(gl.outstandingPrincipal || 0) : '—'}</td>
                  <td style={{ padding: '0.4rem', textAlign: 'right' }}>{closed ? formatCurrency(gl.outstandingInterest || 0) : '—'}</td>
                  <td style={{ padding: '0.4rem', textAlign: 'right' }}>{closed ? formatCurrency(gl.tokenLossCharge || 0) : '—'}</td>
                  <td style={{ padding: '0.4rem', textAlign: 'right', fontWeight: 600 }}>{closed ? formatCurrency(glSettlementOf(gl)) : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem', marginTop: '0.85rem' }}>
          <div><div style={kK}>Closed GL Settlement (P+I+Token)</div><div style={kV}>{formatCurrency(closedGlSettlement)}</div></div>
          <div><div style={kK}>Closed Principal Portion</div><div style={kV}>{formatCurrency(closedPrincipalPortion)} <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: '0.7rem' }}>({(closedRatioSum * 100).toFixed(0)}% of {formatCurrency(addonOutP)})</span></div></div>
          <div><div style={kK}>Total Closing Leg</div><div style={{ ...kV, color: 'var(--danger)' }}>{formatCurrency(totalClosingLeg)}</div></div>
        </div>
      </div>

      {/* ---------------- New Lot Leg ---------------- */}
      <div style={cardStyle('34, 197, 94')}>
        <h3 style={{ fontSize: '0.85rem', fontWeight: 700, color: '#16a34a', margin: '0 0 0.75rem 0' }}>
          New Lot Leg — {record.newLotAccountNumber || record.newRequestRef || '-'}
        </h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
          <thead>
            <tr style={{ background: 'var(--bg-tertiary)' }}>
              <th style={{ ...th, textAlign: 'left' }}>Source</th>
              <th style={{ ...th, textAlign: 'left' }}>GL #</th>
              <th style={{ ...th, textAlign: 'right' }}>Weight</th>
              <th style={{ ...th, textAlign: 'right' }}>Loan Amount</th>
              <th style={{ ...th, textAlign: 'right' }}>Doc Charge</th>
              <th style={{ ...th, textAlign: 'right' }}>Disbursement</th>
              <th style={{ ...th, textAlign: 'right' }}>New Ratio</th>
            </tr>
          </thead>
          <tbody>
            {newLotGLs.map((gl, idx) => {
              const retained = !!gl.sourceMappedGlId;
              const ratio = newTotalWeight > 0 ? ((gl.netWeight || 0) / newTotalWeight) * 100 : 0;
              return (
                <tr key={idx} style={{ borderBottom: '1px solid var(--border-color)', background: retained ? 'rgba(20,184,166,0.05)' : undefined }}>
                  <td style={{ padding: '0.4rem' }}>{retained ? chip('#ccfbf1', '#0f766e', 'Retained') : chip('#dbeafe', '#1d4ed8', 'New')}</td>
                  <td style={{ padding: '0.4rem', fontFamily: 'monospace' }}>{gl.glNumber}</td>
                  <td style={{ padding: '0.4rem', textAlign: 'right' }}>{(gl.netWeight || 0).toFixed(3)}</td>
                  <td style={{ padding: '0.4rem', textAlign: 'right' }}>{retained ? '—' : formatCurrency(gl.originalLoanAmount || 0)}</td>
                  <td style={{ padding: '0.4rem', textAlign: 'right' }}>{retained ? '—' : formatCurrency(gl.documentationCharge || 0)}</td>
                  <td style={{ padding: '0.4rem', textAlign: 'right', fontWeight: 600 }}>{retained ? '—' : formatCurrency(gl.disbursementAmount ?? ((gl.originalLoanAmount || 0) - (gl.documentationCharge || 0)))}</td>
                  <td style={{ padding: '0.4rem', textAlign: 'right' }}>{ratio.toFixed(2)}%</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '0.75rem', marginTop: '0.85rem' }}>
          <div><div style={kK}>Gross Gold Value ({newLotNewGlWeight.toFixed(2)} g × {formatCurrency(record.newLotGoldRatePerGram || 0)})</div><div style={kV}>{formatCurrency(gross)}</div></div>
          <div><div style={kK}>Less New GL Outstanding</div><div style={kV}>{formatCurrency(newGlLoanTotal)}</div></div>
          <div><div style={kK}>New GL Eligibility</div><div style={{ ...kV, color: newGlEligibility < 0 ? '#dc2626' : undefined }}>{formatCurrency(newGlEligibility)}</div></div>
          <div><div style={kK}>Carried Outstanding</div><div style={kV}>{formatCurrency(carried)}</div></div>
          <div><div style={kK}>Eligible Amount</div><div style={kV}>{formatCurrency(eligible)}</div></div>
          <div><div style={kK}>Max Permissible</div><div style={kV}>{formatCurrency(record.newLotMaxPermissible || 0)}</div></div>
          <div><div style={kK}>Requested / Approved Amount</div><div style={{ ...kV, color: 'var(--success)' }}>{formatCurrency(requested)}</div></div>
        </div>
      </div>

      {/* ---------------- Net Cash Flow ---------------- */}
      <div style={{
        borderRadius: '0.5rem', padding: '1rem', textAlign: 'center', marginBottom: '1.25rem',
        border: `2px solid ${ncf >= 0 ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`,
        background: ncf >= 0 ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)',
      }}>
        <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>NET CASH FLOW</div>
        <div style={{ fontSize: '1.4rem', fontWeight: 700, color: ncf >= 0 ? '#16a34a' : '#dc2626' }}>{ncf >= 0 ? '+' : ''}{formatCurrency(ncf)}</div>
        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{ncf >= 0 ? 'Customer receives funds' : 'Customer must bring in shortfall'}</div>
        <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>
          (New Add-On {formatCurrency(requested)} + New GL disbursement {formatCurrency(newGlDisbursement)}) − Closing Leg {formatCurrency(totalClosingLeg)}
        </div>
      </div>

      {/* ---------------- Submission & Approval ---------------- */}
      <div style={cardStyle('59, 130, 246')}>
        <h3 style={{ fontSize: '0.85rem', fontWeight: 700, color: '#2563eb', margin: '0 0 0.75rem 0' }}>Submission &amp; Approval</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem' }}>
          <div><div style={kK}>Submitted By</div><div style={kV}>{record.submittedByName || record.submittedBy || '-'}{record.branch ? ` — ${record.branch}` : ''}</div></div>
          <div><div style={kK}>Submitted At</div><div style={kV}>{formatDate(record.submittedAt)}</div></div>
          <div><div style={kK}>Gold Rate Applied</div><div style={kV}>{formatCurrency(record.newLotGoldRatePerGram || 0)} / g {record.newLotGoldRateBasis ? chip(record.newLotGoldRateBasis === 'FULL' ? '#dbeafe' : '#e2e8f0', record.newLotGoldRateBasis === 'FULL' ? '#1d4ed8' : '#475569', record.newLotGoldRateBasis === 'FULL' ? 'Gross basis' : 'Net basis') : null}</div></div>
          <div><div style={kK}>Rounding Unit</div><div style={kV}>{formatCurrency(roundingUnit)}</div></div>
        </div>
      </div>
    </>
  );
};

// ============================================================================
// Approval Action Modal
// ============================================================================

interface ApprovalActionModalProps {
  isOpen: boolean;
  record: ApprovalItem | null;
  detailLoading?: boolean;
  isProcessing?: boolean;
  onAction: (action: 'approved' | 'returned' | 'rejected', remark: string) => void;
  onClose: () => void;
}

export const ApprovalActionModal: React.FC<ApprovalActionModalProps> = ({
  isOpen,
  record,
  detailLoading = false,
  isProcessing = false,
  onAction,
  onClose,
}) => {
  const [remark, setRemark] = useState('');
  const [showRejectWarning, setShowRejectWarning] = useState(false);

  const { data: loanTypeEnums } = useEnumsByGroup('LOAN_TYPE');
  const loanTypeLookup = useMemo(() => {
    const map: Record<string, string> = {};
    loanTypeEnums.forEach(e => { map[e.enumKey] = e.enumValue; });
    return map;
  }, [loanTypeEnums]);

  const { data: systemConfigs } = useSystemConfig();
  const roundingUnit = useMemo(() => {
    const cfg = systemConfigs.find((c) => c.configKey === 'rounding_unit');
    return cfg ? parseFloat(cfg.configValue) || 5000 : 5000;
  }, [systemConfigs]);

  if (!isOpen || !record) return null;

  const typeStyle = RECORD_TYPE_COLORS[record.recordType] || RECORD_TYPE_COLORS.addon_request;

  const handleReject = () => {
    if (!remark.trim()) return;
    setShowRejectWarning(true);
  };

  const confirmReject = () => {
    onAction('rejected', remark);
    setRemark('');
    setShowRejectWarning(false);
  };

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <h2 className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {record.recordType === 'regroup' ? `Regroup Transaction — ${record.referenceNumber}` : `Review ${record.referenceNumber}`}
            <span style={getStatusBadgeStyle(record.status)}>{(record.status ?? '').replace(/_/g, ' ')}</span>
          </h2>
          <button className="modal-close" onClick={onClose} disabled={isProcessing}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content" style={{ overflow: 'auto', flex: 1 }}>
          {detailLoading && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
              <div style={{ textAlign: 'center', fontSize: '0.9rem' }}>Loading details...</div>
            </div>
          )}
          {!detailLoading && (<>
          {record.recordType === 'regroup' && <RegroupDetailView record={record} roundingUnit={roundingUnit} />}
          {record.recordType !== 'regroup' && (<>
          {/* Request Details */}
          <div style={{ marginBottom: '1.5rem', background: 'var(--bg-secondary)', padding: '1rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)' }}>
            <h3 style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 0.75rem 0', borderBottom: '2px solid var(--primary)', paddingBottom: '0.5rem' }}>
              Request Details
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1rem' }}>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Request Type</div>
                <span style={{
                  padding: '0.3rem 0.65rem', borderRadius: '0.375rem', fontSize: '0.75rem', fontWeight: 600,
                  background: typeStyle.bg, color: typeStyle.color, display: 'inline-block',
                }}>
                  {RECORD_TYPE_LABELS[record.recordType]}
                </span>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Branch</div>
                <div style={{ fontWeight: 500, fontSize: '0.85rem' }}>{record.branch || '-'}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Submitted By</div>
                <div style={{ fontWeight: 500, fontSize: '0.85rem' }}>{record.submittedByName || record.submittedBy}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Submitted On</div>
                <div style={{ fontWeight: 500, fontSize: '0.85rem' }}>{formatDate(record.submittedAt)}</div>
              </div>
            </div>
          </div>

          {/* Add-On Loan Details (common for all types) */}
          <div style={{ marginBottom: '1.5rem', background: 'var(--bg-secondary)', padding: '1rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)' }}>
            <h3 style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 0.75rem 0', borderBottom: '2px solid var(--primary)', paddingBottom: '0.5rem' }}>
              Add-On Loan Details
            </h3>
            {record.recordType === 'combined' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {/* Closing Leg */}
                <div>
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#dc2626', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Closing Leg</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1rem' }}>
                    <div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Loan Type</div>
                      <div style={{ fontWeight: 500, fontSize: '0.85rem' }}>{(record.loanType && loanTypeLookup[record.loanType]) || record.loanType || '-'}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Loan Account Number</div>
                      <div style={{ fontWeight: 500, fontSize: '0.85rem', fontFamily: 'monospace' }}>{record.closingLoanAccountNumber || record.loanAccountNumber || '-'}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Customer ID</div>
                      <div style={{ fontWeight: 500, fontSize: '0.85rem', fontFamily: 'monospace' }}>{record.closingCustomerId || record.customerId}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Customer Name</div>
                      <div style={{ fontWeight: 500, fontSize: '0.85rem' }}>{record.closingCustomerName || record.customerName}</div>
                    </div>
                  </div>
                </div>
                <div style={{ borderTop: '1px solid var(--border-color)' }} />
                {/* New Lot Leg */}
                <div>
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#16a34a', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>New Lot Leg</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1rem' }}>
                    <div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Loan Type</div>
                      <div style={{ fontWeight: 500, fontSize: '0.85rem' }}>{(record.newLotLoanType && loanTypeLookup[record.newLotLoanType]) || record.newLotLoanType || '-'}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Loan Account Number</div>
                      <div style={{ fontWeight: 500, fontSize: '0.85rem', fontFamily: 'monospace' }}>{record.newLotAccountNumber || '-'}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Customer ID</div>
                      <div style={{ fontWeight: 500, fontSize: '0.85rem', fontFamily: 'monospace' }}>{record.newLotCustomerId || record.customerId}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Customer Name</div>
                      <div style={{ fontWeight: 500, fontSize: '0.85rem' }}>{record.newLotCustomerName || record.customerName}</div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1rem' }}>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Loan Type</div>
                  <div style={{ fontWeight: 500, fontSize: '0.85rem' }}>{(record.loanType && loanTypeLookup[record.loanType]) || record.loanType || '-'}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Loan Account Number</div>
                  <div style={{ fontWeight: 500, fontSize: '0.85rem', fontFamily: 'monospace' }}>{record.loanAccountNumber || '-'}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Customer ID</div>
                  <div style={{ fontWeight: 500, fontSize: '0.85rem', fontFamily: 'monospace' }}>{record.customerId}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Customer Name</div>
                  <div style={{ fontWeight: 500, fontSize: '0.85rem' }}>{record.customerName}</div>
                </div>
              </div>
            )}
          </div>

          {/* Summary */}
          <div style={{ marginBottom: '1.5rem', background: 'var(--bg-secondary)', padding: '1rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)' }}>
            <h3 style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 0.75rem 0', borderBottom: '2px solid var(--primary)', paddingBottom: '0.5rem' }}>
              Summary
            </h3>

            {/* Add-On Request Summary */}
            {record.recordType === 'addon_request' && (() => {
              const indicator = getAmountIndicator(record.amount, record.eligibleAmount || 0, record.maximumPermissible || 0);
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
                    <div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Gold Rate</div>
                      <div style={{ fontWeight: 600 }}>
                        {formatCurrency(record.goldRatePerGram || 0)}/g
                        {record.goldRateBasis && (
                          <span style={{ fontSize: '0.62rem', fontWeight: 600, marginLeft: '0.35rem', padding: '0.05rem 0.4rem', borderRadius: '0.625rem',
                            background: record.goldRateBasis === 'FULL' ? '#dbeafe' : '#e2e8f0', color: record.goldRateBasis === 'FULL' ? '#1d4ed8' : '#475569' }}>
                            {record.goldRateBasis === 'FULL' ? 'Gross' : 'Net'}
                          </span>
                        )}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Eligible Amount</div>
                      <div style={{ fontWeight: 600, color: (record.eligibleAmount || 0) > 0 ? '#16a34a' : '#dc2626' }}>{formatCurrency(record.eligibleAmount || 0)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Max Permissible</div>
                      <div style={{ fontWeight: 700, color: 'var(--primary)' }}>{formatCurrency(record.maximumPermissible || 0)}</div>
                    </div>
                  </div>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.75rem',
                    background: 'var(--bg-primary)', borderRadius: '0.375rem', border: `2px solid ${indicator.color}`,
                  }}>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>Requested Amount</div>
                    <div style={{ fontSize: '1.1rem', fontWeight: 700, color: indicator.color }}>{formatCurrency(record.amount)}</div>
                    {indicator.label && <span style={{ fontSize: '0.72rem', color: indicator.color, fontWeight: 600 }}>{indicator.label}</span>}
                  </div>
                </div>
              );
            })()}

            {/* Closing Summary */}
            {record.recordType === 'closing' && (() => {
              const entries = record.closingGLEntries || [];
              const glSettlementPI = entries.reduce((s, gl) => s + gl.outstandingPrincipal + gl.outstandingInterest, 0);
              // CR-001: token loss is a customer-paid charge included in the Total Settlement.
              const totalTokenLoss = entries.reduce((s, gl) => s + (gl.tokenLossCharge ?? 0), 0);
              const totalSettlement = glSettlementPI + totalTokenLoss + (record.addonSettlementPrincipal || 0) + (record.addonSettlementInterest || 0);
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
                    <div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Outstanding Principal</div>
                      <div style={{ fontWeight: 600 }}>{formatCurrency(record.addonOutstandingPrincipal || 0)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Outstanding Interest</div>
                      <div style={{ fontWeight: 600 }}>{formatCurrency(record.addonOutstandingInterest || 0)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Combined Ratio</div>
                      <div style={{ fontWeight: 600, color: 'var(--primary)' }}>{((record.combinedRatio || 0) * 100).toFixed(2)}%</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Settlement Principal</div>
                      <div style={{ fontWeight: 600 }}>{formatCurrency(record.addonSettlementPrincipal || 0)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Settlement Interest</div>
                      <div style={{ fontWeight: 600 }}>{formatCurrency(record.addonSettlementInterest || 0)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>GL Settlement (P+I)</div>
                      <div style={{ fontWeight: 600 }}>{formatCurrency(glSettlementPI)}</div>
                    </div>
                  </div>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.75rem',
                    background: 'var(--bg-primary)', borderRadius: '0.375rem', border: '2px solid #16a34a',
                  }}>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>Total Settlement</div>
                    <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#16a34a' }}>{formatCurrency(totalSettlement)}</div>
                  </div>
                </div>
              );
            })()}

            {/* Combined Summary */}
            {record.recordType === 'combined' && (() => {
              const cGLs = record.closingGLs || [];
              const glSettlementPI = cGLs.reduce((s, gl) => s + gl.outstandingPrincipal + gl.outstandingInterest, 0);
              const totalSettlement = glSettlementPI + (record.addonSettlementPrincipal || 0) + (record.addonSettlementInterest || 0);
              const ncf = Math.round(record.netCashFlow || 0);
              const nlIndicator = getAmountIndicator(record.newLotRequestedAmount || 0, record.newLotEligibleAmount || 0, record.newLotMaxPermissible || 0);
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {/* Closing Leg — same fields as Closing Summary */}
                  <div>
                    <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#dc2626', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Closing Leg</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem', marginBottom: '0.75rem' }}>
                      <div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Outstanding Principal</div>
                        <div style={{ fontWeight: 600 }}>{formatCurrency(record.addonOutstandingPrincipal || 0)}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Outstanding Interest</div>
                        <div style={{ fontWeight: 600 }}>{formatCurrency(record.addonOutstandingInterest || 0)}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Combined Ratio</div>
                        <div style={{ fontWeight: 600, color: 'var(--primary)' }}>{((record.combinedRatio || 0) * 100).toFixed(2)}%</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Settlement Principal</div>
                        <div style={{ fontWeight: 600 }}>{formatCurrency(record.addonSettlementPrincipal || 0)}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Settlement Interest</div>
                        <div style={{ fontWeight: 600 }}>{formatCurrency(record.addonSettlementInterest || 0)}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>GL Settlement (P+I)</div>
                        <div style={{ fontWeight: 600 }}>{formatCurrency(glSettlementPI)}</div>
                      </div>
                    </div>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.6rem 0.75rem',
                      background: 'var(--bg-primary)', borderRadius: '0.375rem', border: '2px solid #dc2626',
                    }}>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>Total Settlement</div>
                      <div style={{ fontSize: '1rem', fontWeight: 700, color: '#dc2626' }}>{formatCurrency(record.closingTotalSettlement || totalSettlement)}</div>
                    </div>
                  </div>
                  <div style={{ borderTop: '1px solid var(--border-color)' }} />
                  {/* New Lot Leg — same fields as Add-On Summary */}
                  <div>
                    <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#16a34a', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>New Lot Leg</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem', marginBottom: '0.75rem' }}>
                      <div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Gold Rate</div>
                        <div style={{ fontWeight: 600 }}>
                          {formatCurrency(record.newLotGoldRatePerGram || 0)}/g
                          {record.newLotGoldRateBasis && (
                            <span style={{ fontSize: '0.62rem', fontWeight: 600, marginLeft: '0.35rem', padding: '0.05rem 0.4rem', borderRadius: '0.625rem',
                              background: record.newLotGoldRateBasis === 'FULL' ? '#dbeafe' : '#e2e8f0', color: record.newLotGoldRateBasis === 'FULL' ? '#1d4ed8' : '#475569' }}>
                              {record.newLotGoldRateBasis === 'FULL' ? 'Gross' : 'Net'}
                            </span>
                          )}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Eligible Amount</div>
                        <div style={{ fontWeight: 600, color: (record.newLotEligibleAmount || 0) > 0 ? '#16a34a' : '#dc2626' }}>{formatCurrency(record.newLotEligibleAmount || 0)}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Max Permissible</div>
                        <div style={{ fontWeight: 700, color: 'var(--primary)' }}>{formatCurrency(record.newLotMaxPermissible || 0)}</div>
                      </div>
                    </div>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.6rem 0.75rem',
                      background: 'var(--bg-primary)', borderRadius: '0.375rem', border: `2px solid ${nlIndicator.color}`,
                    }}>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>Requested Amount</div>
                      <div style={{ fontSize: '1rem', fontWeight: 700, color: nlIndicator.color }}>{formatCurrency(record.newLotRequestedAmount || 0)}</div>
                      {nlIndicator.label && <span style={{ fontSize: '0.72rem', color: nlIndicator.color, fontWeight: 600 }}>{nlIndicator.label}</span>}
                    </div>
                  </div>
                  <div style={{ borderTop: '1px solid var(--border-color)' }} />
                  {/* Net Cash Flow */}
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem', padding: '0.75rem',
                    background: ncf >= 0 ? 'rgba(34, 197, 94, 0.06)' : 'rgba(239, 68, 68, 0.06)',
                    borderRadius: '0.375rem', border: `2px solid ${ncf >= 0 ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
                  }}>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>Net Cash Flow</div>
                    <div style={{ fontSize: '1.1rem', fontWeight: 700, color: ncf >= 0 ? '#16a34a' : '#dc2626' }}>
                      {ncf >= 0 ? '+' : ''}{formatCurrency(ncf)}
                    </div>
                    <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>
                      {ncf >= 0 ? 'Customer receives funds' : 'Customer must bring in shortfall'}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* GL Replacement Summary */}
            {record.recordType === 'gl_replacement' && (() => {
              const outEntries = record.replacementOutgoingEntries || [];
              const inEntries = record.replacementIncomingEntries || [];
              const ncf = Math.round(record.netCashFlow || 0);
              const eTh: React.CSSProperties = { padding: '0.4rem', fontWeight: 600, borderBottom: '2px solid var(--border-color)' };
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {/* Weight Analysis */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
                    <div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Outgoing Weight</div>
                      <div style={{ fontWeight: 600 }}>{(record.totalOutgoingWeight || 0).toFixed(3)}g</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Incoming Weight</div>
                      <div style={{ fontWeight: 600 }}>{(record.totalIncomingWeight || 0).toFixed(3)}g</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Weight Difference</div>
                      <div style={{ fontWeight: 600, color: (record.weightDifference || 0) > 0 ? '#dc2626' : '#16a34a' }}>
                        {(record.weightDifference || 0).toFixed(3)}g
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Total Weight Before</div>
                      <div style={{ fontWeight: 600 }}>{(record.totalWeightBefore || 0).toFixed(3)}g</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Settlement Ratio</div>
                      <div style={{ fontWeight: 600, color: 'var(--primary)' }}>{((record.settlementRatio || 0) * 100).toFixed(4)}%</div>
                    </div>
                  </div>
                  {/* Settlement */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem' }}>
                    <div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Add-On Settlement Principal</div>
                      <div style={{ fontWeight: 600 }}>{formatCurrency(record.addonSettlementPrincipal || 0)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Add-On Settlement Interest</div>
                      <div style={{ fontWeight: 600 }}>{formatCurrency(record.addonSettlementInterest || 0)}</div>
                      <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>Full Amount</div>
                    </div>
                  </div>
                  {/* Outgoing Entries Table */}
                  {outEntries.length > 0 && (
                    <div style={{ overflowX: 'auto' }}>
                      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#dc2626', marginBottom: '0.3rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Outgoing GL (Being Removed)</div>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                        <thead>
                          <tr style={{ background: 'var(--bg-tertiary)' }}>
                            <th style={{ ...eTh, textAlign: 'left' }}>GL Number</th>
                            <th style={{ ...eTh, textAlign: 'left' }}>Customer</th>
                            <th style={{ ...eTh, textAlign: 'right' }}>Weight (g)</th>
                            <th style={{ ...eTh, textAlign: 'right' }}>Alloc. Ratio</th>
                            <th style={{ ...eTh, textAlign: 'right' }}>Principal</th>
                            <th style={{ ...eTh, textAlign: 'right' }}>Interest</th>
                          </tr>
                        </thead>
                        <tbody>
                          {outEntries.map((e, idx) => (
                            <tr key={idx} style={{ borderBottom: '1px solid var(--border-color)' }}>
                              <td style={{ padding: '0.4rem', fontFamily: 'monospace' }}>{e.outgoingGlNumber}</td>
                              <td style={{ padding: '0.4rem' }}>{e.outgoingCustomerName || '—'}</td>
                              <td style={{ padding: '0.4rem', textAlign: 'right' }}>{e.outgoingNetWeight.toFixed(3)}</td>
                              <td style={{ padding: '0.4rem', textAlign: 'right' }}>{((e.outgoingAllocationRatio || 0) * 100).toFixed(2)}%</td>
                              <td style={{ padding: '0.4rem', textAlign: 'right' }}>{formatCurrency(e.outgoingGlOutstandingPrincipal)}</td>
                              <td style={{ padding: '0.4rem', textAlign: 'right' }}>{formatCurrency(e.outgoingGlOutstandingInterest)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {/* Incoming Entries Table */}
                  {inEntries.length > 0 && (
                    <div style={{ overflowX: 'auto' }}>
                      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#16a34a', marginBottom: '0.3rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Incoming GL (New Replacement)</div>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                        <thead>
                          <tr style={{ background: 'var(--bg-tertiary)' }}>
                            <th style={{ ...eTh, textAlign: 'left' }}>GL Number</th>
                            <th style={{ ...eTh, textAlign: 'left' }}>Customer</th>
                            <th style={{ ...eTh, textAlign: 'right' }}>Weight (g)</th>
                            <th style={{ ...eTh, textAlign: 'right' }}>Loan Amount</th>
                            <th style={{ ...eTh, textAlign: 'left' }}>Loan Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {inEntries.map((e, idx) => (
                            <tr key={idx} style={{ borderBottom: '1px solid var(--border-color)' }}>
                              <td style={{ padding: '0.4rem', fontFamily: 'monospace' }}>{e.incomingGlNumber}</td>
                              <td style={{ padding: '0.4rem' }}>{e.incomingCustomerName ? `${e.incomingCustomerId} - ${e.incomingCustomerName}` : (e.incomingCustomerId || '—')}</td>
                              <td style={{ padding: '0.4rem', textAlign: 'right' }}>{e.incomingNetWeight.toFixed(3)}</td>
                              <td style={{ padding: '0.4rem', textAlign: 'right' }}>{formatCurrency(e.incomingOriginalLoanAmount)}</td>
                              <td style={{ padding: '0.4rem' }}>{e.incomingLoanDate ? formatDate(e.incomingLoanDate) : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {/* Net Cash Flow */}
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem', padding: '0.75rem',
                    background: ncf >= 0 ? 'rgba(34, 197, 94, 0.06)' : 'rgba(239, 68, 68, 0.06)',
                    borderRadius: '0.375rem', border: `2px solid ${ncf >= 0 ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
                  }}>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>Net Cash Flow</div>
                    <div style={{ fontSize: '1.1rem', fontWeight: 700, color: ncf >= 0 ? '#16a34a' : '#dc2626' }}>
                      {ncf >= 0 ? '+' : ''}{formatCurrency(ncf)}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Module-Specific Detail View */}
          {record.recordType === 'addon_request' && <AddonRequestDetailView record={record} />}
          {record.recordType === 'closing' && <ClosingDetailView record={record} />}
          {record.recordType === 'combined' && <CombinedDetailView record={record} />}

          {/* Mapping Reference (for closing, combined, gl_replacement) */}
          {(record.recordType === 'closing' || record.recordType === 'combined' || record.recordType === 'gl_replacement') && (
            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: record.recordType === 'combined' ? '1fr 1fr' : '1fr', gap: '1rem' }}>
                <div>
                  <label className="form-label">Mapping Reference</label>
                  <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', fontFamily: 'monospace' }}>
                    {(record.recordType === 'combined' ? record.closingMappingRef : record.mappingRef) || '-'}
                  </div>
                </div>
              </div>
            </div>
          )}

          </>)}
          {/* Workflow Information */}
          <div style={{ marginBottom: '1.5rem', borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
            <h3 style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 0.75rem 0', borderBottom: '2px solid var(--primary)', paddingBottom: '0.5rem' }}>
              Workflow Information
            </h3>
            {record.returnedHistory && record.returnedHistory.length > 0 ? (
              <div style={{ marginBottom: '1rem' }}>
                <label className="form-label" style={{ marginBottom: '0.5rem' }}>Returned History</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {record.returnedHistory.map((entry, idx) => (
                    <div key={idx} style={{
                      padding: '0.625rem 0.875rem', background: 'rgba(245, 158, 11, 0.06)',
                      border: '1px solid rgba(245, 158, 11, 0.2)', borderRadius: '0.375rem',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.35rem' }}>
                        <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#92400e' }}>
                          Returned by {entry.returnedBy}
                        </span>
                        <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                          {formatDate(entry.returnedAt)}
                        </span>
                      </div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                        {entry.remark}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{ padding: '0.75rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', marginBottom: '1rem' }}>
                <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>No returned history</span>
              </div>
            )}
          </div>

          {/* Rejection Warning */}
          {showRejectWarning && (
            <div style={{
              background: 'rgba(239, 68, 68, 0.08)', border: '2px solid rgba(239, 68, 68, 0.4)',
              borderRadius: '0.5rem', padding: '1rem', marginBottom: '1rem',
              display: 'flex', flexDirection: 'column', gap: '0.75rem',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <IconSystem.alert size={20} style={{ color: '#dc2626', flexShrink: 0 }} />
                <div>
                  <div style={{ fontWeight: 700, color: '#dc2626', fontSize: '0.85rem' }}>Irreversible Action</div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                    This rejection cannot be reverted. The record will be permanently marked as rejected and the submitter will need to create a new request.
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button className="btn btn-secondary" style={{ fontSize: '0.8rem' }} onClick={() => setShowRejectWarning(false)} disabled={isProcessing}>
                  Go Back
                </button>
                <button
                  className="btn"
                  style={{ background: '#dc2626', color: '#fff', border: 'none', fontSize: '0.8rem', opacity: isProcessing ? 0.6 : 1 }}
                  onClick={confirmReject}
                  disabled={isProcessing}
                >
                  Yes, Reject Permanently
                </button>
              </div>
            </div>
          )}

          {/* Remark */}
          {!showRejectWarning && (
            <div className="form-group">
              <label className="form-label">Remark (required for Return)</label>
              <textarea
                className="form-input"
                rows={3}
                placeholder="Enter remark or feedback..."
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
                style={{ resize: 'vertical' }}
                disabled={isProcessing}
              />
            </div>
          )}
          </>)}
        </div>

        {!showRejectWarning && (
          <div className="modal-footer" style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
            <button className="btn btn-secondary" onClick={onClose} disabled={isProcessing}>Cancel</button>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              {isProcessing && (
                <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>Processing...</span>
              )}
              <button
                className="btn"
                style={{ background: '#dc2626', color: '#fff', border: 'none', opacity: isProcessing ? 0.6 : 1 }}
                onClick={handleReject}
                disabled={!remark.trim() || isProcessing}
                title={!remark.trim() ? 'Remark is required for reject' : 'Reject this item permanently'}
              >
                Reject
              </button>
              <button
                className="btn"
                style={{ background: '#f59e0b', color: '#fff', border: 'none', opacity: isProcessing ? 0.6 : 1 }}
                onClick={() => {
                  if (!remark.trim()) return;
                  onAction('returned', remark);
                  setRemark('');
                }}
                disabled={!remark.trim() || isProcessing}
                title={!remark.trim() ? 'Remark is required for return' : 'Return for correction'}
              >
                Return
              </button>
              <button
                className="btn"
                style={{ background: '#16a34a', color: '#fff', border: 'none', opacity: isProcessing ? 0.6 : 1 }}
                onClick={() => {
                  onAction('approved', remark);
                  setRemark('');
                }}
                disabled={isProcessing}
                title="Approve this item"
              >
                Approve
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
